import { loggerDecorator } from '../../../interface/v1/clientOsLogger/decorators';
import { timeout } from '../../../utils/withTimeout';
import { jWebStates } from '../Initializer';
import { JWebType, JWebWindow } from '../types';

export enum EnvironmentStatusEnum {
  web = 'web',
  native = 'native',
  notReady = 'notReady',
  unknown = 'unknown'
}

let isNativeJWeb = EnvironmentStatusEnum.notReady;
export { isNativeJWeb };

/**
 * Core facade for basic JWeb operations
 */
export class JWebCoreFacade {
  /**
   * Cached platform promise
   */
  private static cachedPlatformPromise: Promise<string> | null = null;

  /**
   * Get the JWeb instance after ensuring it's ready
   */
  public static async getJWeb(): Promise<JWebType> {
    // @TODO: Handle timeout for jWebReady?
    await jWebStates.JWebLibrary;
    return (window as JWebWindow)?.JWeb;
  }

  /**
   * Check if the current environment is native
   */
  @timeout({ message: 'JWeb: isNative() timed out' })
  public static async isNative(): Promise<boolean> {
    const result = (await this.getJWeb())?.isNative;
    if (result === undefined) {
      console.warn('JWeb: isNative is undefined');
      isNativeJWeb = EnvironmentStatusEnum.unknown;
      return result;
    }
    isNativeJWeb = result
      ? EnvironmentStatusEnum.native
      : EnvironmentStatusEnum.web;

    return result;
  }

  /**
   * Check sync if the current environment is native
   */
  @loggerDecorator.debug.d3()
  public static isNativeSync(): boolean {
    if (isNativeJWeb === EnvironmentStatusEnum.notReady) {
      console.warn('JWeb: isNativeSync called before JWeb is ready');
      return false;
    }
    return isNativeJWeb === EnvironmentStatusEnum.native;
  }

  /**
   * Get the current platform
   */
  @loggerDecorator.debug.d3()
  @timeout({ message: 'JWeb: getPlatform() timed out' })
  public static async getPlatform(): Promise<string> {
    return (await this.getJWeb())?.platform;
  }

  /**
   * Get the current platform (cached).
   * If the cache is empty, it fetches the platform and caches it.
   * If an error occurs, the cache is cleared so the next call will retry.
   */
  @loggerDecorator.debug.d3()
  public static async getPlatformCached(): Promise<string> {
    if (this.cachedPlatformPromise == null) {
      this.cachedPlatformPromise = this.getPlatform().catch((error) => {
        this.cachedPlatformPromise = null;
        throw error;
      });
    }

    return this.cachedPlatformPromise;
  }

  /**
   * Handle JWeb errors
   */
  public static JWebErrorHandler<T>(object: Record<string, unknown>): T {
    if (object?.errorType) {
      throw new Error(
        `JWeb: ${object?.errorType as string} - ${object?.reason as string}`
      );
    }
    return object as T;
  }
}
